import { createContext, useCallback, useReducer } from 'react'
import { ResourceType } from '../../resources/types'

export interface ResourceListContextType {
  onSelectRowById: (id: string) => void
  onSelectAll: (listIds: string[]) => void
  onClearSelections: () => void
  resourceType: ResourceType
  requestParams: any
  bulkSelectionIds: string[]
}

export const ResourceListContext = createContext<ResourceListContextType>({
  onSelectRowById: () => {},
  onSelectAll: () => {},
  onClearSelections: () => {},
  resourceType: 'SPACES',
  requestParams: {},
  bulkSelectionIds: [],
})

/**
 * Simple state reducer for managing current selections within the <ResourceList />
 * table
 *
 * @param bulkSelectionIds - list of all currently selected ids
 * @param action - dispatched action to trigger state change
 * @returns
 */
const ResourceListReducer = (bulkSelectionIds: string[], action: any) => {
  switch (action.type) {
    case 'toggleRow': {
      const selectedId = action.id
      return bulkSelectionIds.includes(selectedId)
        ? bulkSelectionIds.filter((item: string) => item !== selectedId)
        : [...bulkSelectionIds, selectedId]
    }
    case 'toggleAll': {
      const resourceListIds = action.resourceIds
      return bulkSelectionIds.length === resourceListIds.length ? [] : [...resourceListIds]
    }
    case 'clearAll': {
      return []
    }
    default: {
      throw Error(`Unknown action: ${action.type}`)
    }
  }
}

/**
 * Utility context specific to the <ResourceList /> component which aims
 * to give easier access to list-level properties without the need for
 * excessive prop drilling or redundancy
 *
 * Primary capabilities:
 * - Easy access to bulk selections which is needed for both the table, and actions
 * @param requestParams - Request params driving the content of the list
 * @param resourceType - Resource type of the list
 * @returns
 */
export const ResourceListContextProvider = ({
  requestParams,
  resourceType,
  children,
}: {
  requestParams: any
  resourceType: ResourceType
  children: React.ReactNode
}) => {
  const [bulkSelectionIds, dispatch] = useReducer(ResourceListReducer, [])

  const onSelectRowById = useCallback((id: string) => {
    dispatch({
      type: 'toggleRow',
      id,
    })
  }, [])

  const onSelectAll = useCallback((currentIds: string[]) => {
    dispatch({
      type: 'toggleAll',
      resourceIds: currentIds,
    })
  }, [])

  const onClearSelections = useCallback(() => {
    dispatch({
      type: 'clearAll',
    })
  }, [])

  return (
    <ResourceListContext.Provider
      value={{
        onSelectRowById,
        onSelectAll,
        onClearSelections,
        bulkSelectionIds,
        resourceType,
        requestParams,
      }}
    >
      {children}
    </ResourceListContext.Provider>
  )
}
